/**
 * VideoEmbed -- In-app content player for the Watch module.
 *
 * SAFETY: All content plays INSIDE AWENI. No external links, no new tabs,
 * no leaving the app. This is a walled-garden requirement for child safety.
 *
 * Supports:
 * - YouTube privacy-enhanced embeds (youtube-nocookie.com)
 * - Generic sandboxed iframes for non-YouTube embeddable content
 * - "Cannot embed" fallback with copy-link option
 * - Loading indicator with error handling
 * - Fullscreen toggle
 * - Link interception (prevents any navigation outside the app)
 */

import type { ContentItem } from './ContentCatalog';
import { isEmbeddable } from './ContentCatalog';

/** Timeout (ms) to detect iframe load failure */
const IFRAME_LOAD_TIMEOUT = 15_000;

export class VideoEmbed {
  private container: HTMLElement | null = null;
  private onBackCallback: (() => void) | null = null;
  private isFullscreen = false;
  private fullscreenOverlay: HTMLElement | null = null;

  render(
    container: HTMLElement,
    item: ContentItem,
    locale: string,
    t: (key: string) => string,
    isRtl: boolean,
    onBack: () => void,
  ): void {
    this.container = container;
    this.onBackCallback = onBack;

    const title = item.title[locale] || item.title.en || '';
    const description = item.description[locale] || item.description.en || '';
    const canEmbed = isEmbeddable(item);

    container.innerHTML = '';

    const wrapper = document.createElement('div');
    wrapper.className = 'video-embed-view';
    wrapper.style.cssText = 'max-width:640px;margin:0 auto;padding:0 16px 100px;';
    wrapper.setAttribute('dir', isRtl ? 'rtl' : 'ltr');

    // -- Intercept ALL clicks on <a> tags inside this view --
    this._interceptLinks(wrapper, t);

    // Back button
    const backBtn = document.createElement('button');
    backBtn.className = 'flex items-center gap-2 mb-4 text-sm font-medium';
    backBtn.style.cssText = `color:var(--accent);background:none;border:none;cursor:pointer;padding:8px 0;`;
    backBtn.innerHTML = `
      <svg class="w-4 h-4 ${isRtl ? 'rotate-180' : ''}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
      ${t('watch_back_to_browse')}
    `;
    backBtn.addEventListener('click', () => {
      this._exitFullscreen();
      this.destroy();
      if (this.onBackCallback) this.onBackCallback();
    });
    wrapper.appendChild(backBtn);

    // Player area
    if (canEmbed && item.embed_url) {
      // Has a direct embed URL (YouTube or other known embed-friendly source)
      this._renderEmbedPlayer(wrapper, item, title, t, isRtl);
    } else if (canEmbed && !item.embed_url) {
      // Embeddable via sandboxed iframe using the main URL
      this._renderSandboxedPlayer(wrapper, item, title, t, isRtl);
    } else {
      // Cannot be embedded -- show notice with copy-link
      this._renderCannotEmbed(wrapper, item, t);
    }

    // Content info section
    const info = document.createElement('div');
    info.innerHTML = `
      <h2 class="text-lg font-bold mb-2" style="color:var(--text-primary);">${this._escapeHtml(title)}</h2>
      <div class="flex items-center gap-2 flex-wrap mb-3">
        <span class="px-3 py-1 rounded-full text-xs font-medium" style="background:${this._getSourceColor(item.source)};color:white;">${this._escapeHtml(item.source)}</span>
        ${item.duration ? `<span class="text-xs" style="color:var(--text-secondary);">${item.duration} min</span>` : ''}
        ${!item.duration && (item.type === 'live_tv' || item.type === 'live_radio') ? `<span class="px-2 py-0.5 rounded-full text-xs font-bold" style="background:#EF4444;color:white;">LIVE</span>` : ''}
        <span class="text-xs" style="color:var(--text-secondary);">${item.languages.map((l) => this._langFlag(l)).join(' ')}</span>
      </div>
      <p class="text-sm leading-relaxed mb-4" style="color:var(--text-secondary);">${this._escapeHtml(description)}</p>
      <div class="flex gap-2 flex-wrap">
        ${item.tags.map((tag) => `<span class="px-2 py-1 rounded-full text-xs" style="background:var(--bg-secondary);color:var(--text-secondary);">#${tag}</span>`).join('')}
      </div>
    `;
    wrapper.appendChild(info);

    container.appendChild(wrapper);
  }

  destroy(): void {
    this._exitFullscreen();
    if (this.container) {
      this.container.innerHTML = '';
    }
    this.container = null;
    this.onBackCallback = null;
  }

  // ---------- Embed Players ----------

  /**
   * YouTube or known embed-friendly content with a dedicated embed URL.
   * Uses standard iframe with fullscreen support.
   */
  private _renderEmbedPlayer(
    wrapper: HTMLElement,
    item: ContentItem,
    title: string,
    t: (key: string) => string,
    isRtl: boolean,
  ): void {
    const videoWrap = document.createElement('div');
    videoWrap.className = 'video-embed-player';
    videoWrap.style.cssText = `
      position: relative; width: 100%; padding-bottom: 56.25%; /* 16:9 */
      border-radius: 16px; overflow: hidden; background: #000;
      margin-bottom: 16px;
    `;

    // Loading skeleton
    const skeleton = this._createSkeleton(t);
    videoWrap.appendChild(skeleton);

    // iframe -- YouTube embeds are trusted, so we use standard permissions
    const iframe = document.createElement('iframe');
    iframe.src = item.embed_url!;
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen');
    // Sandbox for YouTube: allow scripts + same-origin + presentation
    // NO allow-popups, NO allow-top-navigation
    iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-presentation');
    iframe.setAttribute('loading', 'lazy');
    iframe.setAttribute('title', title);
    iframe.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;border:none;z-index:1;';

    // Load success
    iframe.addEventListener('load', () => {
      skeleton.style.display = 'none';
    });

    // Load failure timeout
    const loadTimer = setTimeout(() => {
      if (skeleton.style.display !== 'none') {
        skeleton.innerHTML = `
          <div style="text-align:center;padding:16px;">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--text-secondary)" stroke-width="2" style="margin:0 auto 8px;"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
            <p class="text-xs" style="color:var(--text-secondary);">${t('watch_cannot_embed')}</p>
          </div>
        `;
      }
    }, IFRAME_LOAD_TIMEOUT);

    iframe.addEventListener('load', () => clearTimeout(loadTimer));

    videoWrap.appendChild(iframe);

    // Fullscreen toggle button
    const fsBtn = this._createFullscreenButton(videoWrap, t, isRtl);
    videoWrap.appendChild(fsBtn);

    wrapper.appendChild(videoWrap);
  }

  /**
   * Non-YouTube content that might be embeddable.
   * Uses a heavily sandboxed iframe to prevent escape.
   */
  private _renderSandboxedPlayer(
    wrapper: HTMLElement,
    item: ContentItem,
    title: string,
    t: (key: string) => string,
    isRtl: boolean,
  ): void {
    const videoWrap = document.createElement('div');
    videoWrap.className = 'video-embed-player';
    videoWrap.style.cssText = `
      position: relative; width: 100%; padding-bottom: 56.25%;
      border-radius: 16px; overflow: hidden; background: #000;
      margin-bottom: 16px;
    `;

    // Loading skeleton
    const skeleton = this._createSkeleton(t);
    videoWrap.appendChild(skeleton);

    // Sandboxed iframe -- STRICT restrictions
    const iframe = document.createElement('iframe');
    iframe.src = item.url;
    // CRITICAL sandbox: allow-scripts + allow-same-origin ONLY
    // NO allow-popups -- prevents ads/popups
    // NO allow-top-navigation -- prevents navigating parent away
    // NO allow-popups-to-escape-sandbox -- prevents sandbox escape
    iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin');
    iframe.setAttribute('allow', 'autoplay; encrypted-media; fullscreen');
    iframe.setAttribute('loading', 'lazy');
    iframe.setAttribute('title', title);
    iframe.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;border:none;z-index:1;';

    // On load -- hide skeleton
    iframe.addEventListener('load', () => {
      skeleton.style.display = 'none';
    });

    // Error detection: if X-Frame-Options blocks, iframe won't fire load with content.
    // We use a timeout to detect this.
    const loadTimer = setTimeout(() => {
      if (skeleton.style.display !== 'none') {
        // Failed to load -- show cannot-embed fallback
        videoWrap.innerHTML = '';
        this._renderCannotEmbed(wrapper, item, t);
        // Remove the empty videoWrap
        videoWrap.remove();
      }
    }, IFRAME_LOAD_TIMEOUT);

    iframe.addEventListener('load', () => clearTimeout(loadTimer));

    videoWrap.appendChild(iframe);

    // Fullscreen toggle
    const fsBtn = this._createFullscreenButton(videoWrap, t, isRtl);
    videoWrap.appendChild(fsBtn);

    wrapper.appendChild(videoWrap);
  }

  /**
   * Content that cannot be embedded (X-Frame-Options blocks it, or marked non-embeddable).
   * Shows a notice with a copy-link button. NEVER opens external links automatically.
   */
  private _renderCannotEmbed(
    wrapper: HTMLElement,
    item: ContentItem,
    t: (key: string) => string,
  ): void {
    const card = document.createElement('div');
    card.style.cssText = `
      border-radius: 16px; padding: 24px; margin-bottom: 16px; text-align: center;
      background: linear-gradient(135deg, var(--accent), color-mix(in srgb, var(--accent) 60%, #000));
      color: white;
    `;

    card.innerHTML = `
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="1.5" style="margin:0 auto 12px;">
        <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"/>
        <line x1="7" y1="2" x2="7" y2="22"/>
        <line x1="17" y1="2" x2="17" y2="22"/>
        <line x1="2" y1="12" x2="22" y2="12"/>
      </svg>
      <p class="text-sm font-bold mb-2">${t('watch_cannot_embed')}</p>
      <p class="text-xs mb-4" style="color:rgba(255,255,255,0.8);">
        ${t('watch_cannot_embed_detail') || this._escapeHtml(item.source)}
      </p>
      <button class="video-embed-copy-link inline-block px-6 py-3 rounded-xl text-sm font-bold" style="background:white;color:var(--accent);border:none;cursor:pointer;">
        ${t('watch_copy_link')}
      </button>
    `;

    // Copy link button -- does NOT open external browser
    const copyBtn = card.querySelector('.video-embed-copy-link') as HTMLButtonElement;
    copyBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      navigator.clipboard?.writeText(item.url).then(() => {
        copyBtn.textContent = '\u2713 Copied!';
        setTimeout(() => {
          copyBtn.textContent = t('watch_copy_link');
        }, 2000);
      }).catch(() => {
        // Fallback: select text
        const input = document.createElement('input');
        input.value = item.url;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
        copyBtn.textContent = '\u2713 Copied!';
        setTimeout(() => {
          copyBtn.textContent = t('watch_copy_link');
        }, 2000);
      });
    });

    wrapper.appendChild(card);
  }

  // ---------- Fullscreen ----------

  private _createFullscreenButton(
    videoWrap: HTMLElement,
    t: (key: string) => string,
    isRtl: boolean,
  ): HTMLElement {
    const btn = document.createElement('button');
    btn.className = 'video-embed-fs-btn';
    btn.setAttribute('aria-label', t('watch_fullscreen'));
    btn.style.cssText = `
      position:absolute;bottom:8px;${isRtl ? 'left' : 'right'}:8px;z-index:10;
      width:32px;height:32px;border-radius:8px;border:none;
      background:rgba(0,0,0,0.6);color:white;cursor:pointer;
      display:flex;align-items:center;justify-content:center;
      transition:background var(--motion-duration-base);
    `;
    btn.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>`;

    btn.addEventListener('mouseenter', () => { btn.style.background = 'rgba(0,0,0,0.8)'; });
    btn.addEventListener('mouseleave', () => { btn.style.background = 'rgba(0,0,0,0.6)'; });

    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (this.isFullscreen) {
        this._exitFullscreen();
      } else {
        this._enterFullscreen(videoWrap, t);
      }
    });

    return btn;
  }

  private _enterFullscreen(videoWrap: HTMLElement, t: (key: string) => string): void {
    // Try native fullscreen first
    if (videoWrap.requestFullscreen) {
      videoWrap.requestFullscreen().catch(() => {
        // Fallback to manual overlay
        this._showFullscreenOverlay(videoWrap, t);
      });
    } else {
      this._showFullscreenOverlay(videoWrap, t);
    }
    this.isFullscreen = true;
  }

  private _showFullscreenOverlay(videoWrap: HTMLElement, t: (key: string) => string): void {
    this.fullscreenOverlay = document.createElement('div');
    this.fullscreenOverlay.style.cssText = `
      position:fixed;inset:0;z-index:9999;background:#000;
      display:flex;align-items:center;justify-content:center;
    `;

    // Clone iframe into overlay
    const iframe = videoWrap.querySelector('iframe');
    if (iframe) {
      const clone = iframe.cloneNode(true) as HTMLIFrameElement;
      clone.style.cssText = 'width:100%;height:100%;border:none;';
      this.fullscreenOverlay.appendChild(clone);
    }

    // Exit fullscreen button
    const exitBtn = document.createElement('button');
    exitBtn.setAttribute('aria-label', t('watch_exit_fullscreen'));
    exitBtn.style.cssText = `
      position:absolute;top:16px;right:16px;z-index:10;
      width:40px;height:40px;border-radius:50%;border:none;
      background:rgba(255,255,255,0.2);color:white;cursor:pointer;
      display:flex;align-items:center;justify-content:center;
    `;
    exitBtn.innerHTML = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`;
    exitBtn.addEventListener('click', () => this._exitFullscreen());
    this.fullscreenOverlay.appendChild(exitBtn);

    document.body.appendChild(this.fullscreenOverlay);
  }

  private _exitFullscreen(): void {
    this.isFullscreen = false;
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    }
    if (this.fullscreenOverlay) {
      this.fullscreenOverlay.remove();
      this.fullscreenOverlay = null;
    }
  }

  // ---------- Link Interception ----------

  /**
   * Intercept ALL anchor clicks inside the player view.
   * NEVER open external links automatically.
   */
  private _interceptLinks(container: HTMLElement, t: (key: string) => string): void {
    container.addEventListener('click', (e) => {
      const anchor = (e.target as HTMLElement).closest('a');
      if (anchor && anchor.href) {
        e.preventDefault();
        e.stopPropagation();
        // Show a toast/notification instead of opening the link
        const url = anchor.href;
        navigator.clipboard?.writeText(url).catch(() => {});
        // Brief visual feedback
        const toast = document.createElement('div');
        toast.style.cssText = `
          position:fixed;bottom:80px;left:50%;transform:translateX(-50%);z-index:10000;
          background:var(--card-bg);color:var(--text-primary);padding:10px 20px;
          border-radius:12px;box-shadow:0 4px 16px rgba(0,0,0,0.2);font-size:13px;
          animation:slideUp var(--motion-duration-slow) var(--motion-easing-default);
        `;
        toast.textContent = t('watch_copy_link') + ' \u2713';
        document.body.appendChild(toast);
        setTimeout(() => toast.remove(), 2500);
      }
    });
  }

  // ---------- Utility ----------

  private _createSkeleton(t: (key: string) => string): HTMLElement {
    const skeleton = document.createElement('div');
    skeleton.style.cssText = `
      position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
      background: var(--bg-secondary);
    `;
    skeleton.innerHTML = `
      <div style="text-align:center;">
        <div class="aweni-spinner" style="margin:0 auto 8px;"></div>
        <p class="text-xs" style="color:var(--text-secondary);">${t('common_loading')}</p>
      </div>
    `;
    return skeleton;
  }

  private _escapeHtml(text: string): string {
    const d = document.createElement('div');
    d.textContent = text;
    return d.innerHTML;
  }

  private _getSourceColor(source: string): string {
    const colors: Record<string, string> = {
      YouTube: '#FF0000', 'TED': '#E62B1E', 'TED-Ed': '#E62B1E',
      'Khan Academy': '#14BF96', BBC: '#BB1919', 'BBC Earth': '#4A8C2A',
      DW: '#003D7A', 'Al Jazeera': '#D2A026', 'National Geographic': '#FFCE00',
      'France 24': '#0055A4', Arte: '#FA5400', NPR: '#6B6B6B',
      'France Inter': '#E4002B', Euronews: '#003399', RFI: '#007BB8',
      Kurzgesagt: '#2196F3', CrashCourse: '#4CAF50',
    };
    return colors[source] || 'var(--accent)';
  }

  private _langFlag(lang: string): string {
    const flags: Record<string, string> = {
      en: '\uD83C\uDDEC\uD83C\uDDE7', fr: '\uD83C\uDDEB\uD83C\uDDF7',
      ar: '\uD83C\uDDF8\uD83C\uDDE6', es: '\uD83C\uDDEA\uD83C\uDDF8',
      de: '\uD83C\uDDE9\uD83C\uDDEA',
    };
    return flags[lang] || lang;
  }
}
